import { AddCartProductInfo } from "./Cart";

declare var ga: any;
declare var gtag: any;

interface UserDataJson {
    email?: string;
    phone_number: string;
    address?: {
        first_name?: string;
        last_name?: string;
        street?: string;
        city?: string;
        postal_code?: string;
        country?: string;
    };
}

export interface JsProductInfo {
    id: number;
    cartid: string;
    name?: string;

    currency?: string;
    price?: string;

    category?: string;
}

interface PurchaseInfo {
    order_id: number;

    currency: string;
    totalprice: string;
    shippingprice: string;
    tax: string;

    products: {
        id: string;
        title: string;
        sku: string;
        price?: string;
        quantity: number;

        category?: {
            title: string;
        },
    }[];

    user?: UserDataJson;
}

interface CategoryViewJson {
    listname: string;
    currency: string;

    products: JsProductInfo[];
}

export interface CartJson {
    totalprice: string;
    products: AddCartProductInfo[];
}

interface PageJson {
    product_ids?: number[] | string[];
    totalprice?: number;
    quantity?: number;

    page: {
        title?: string;
        type?: string;
    };

    user?: UserDataJson;
}

export default class Tracking {
    private static accepted: boolean = false;
    private static actions: (() => void)[] = [];

    public static doAction(action: () => void): void {
        if (this.accepted) {
            action();
        } else {
            this.actions.push(action);
        }
    }

    private static pushDlEvent(event: string, ecommerce: any): void {
        this.doAction(() => {
            console.log('dataLayer', event, ecommerce);

            (<any>window).dataLayer = (<any>window).dataLayer || [];

            const args = {
                'event': event,
                'ecommerce': ecommerce,
            };

            (<any>window).dataLayer.push(args);
        });
    }

    private static fbTrack(name: string, args?: any) {
        this.doAction(() => {
            console.log('fbq', name, args);

            if ((<any>window).fbq != null) {
                (<any>window).fbq('track', name, args);
            }
        });
    }

    private static ecomm(google_tag_params: any): void {
        this.doAction(() => {
            console.log('ecomm', google_tag_params);

            (<any>window).dataLayer = (<any>window).dataLayer || [];
            (<any>window).dataLayer.push(google_tag_params);
        });
    }

    public static gtag(name1: string, name2: string, data?: any): void {
        this.doAction(() => {
            console.log('gtag', name1, name2, data);

            if ((<any>window).gtag) {
                console.log('gtagok');
                gtag(name1, name2, data);
            }
        });
    }

    public static eventPage(data?: PageJson): void {
        if (data == null) {
            return;
        }

        if (data.page.type && data.product_ids) {
            this.ecomm({
                ecomm_prodid: data.product_ids,
                ecomm_totalvalue: data.totalprice,

                ecomm_pagetype: data.page.type,
                ecomm_pagetitle: data.page.title,
            });
        }

        if (data.user) {
            this.pushDlEvent('user_data', {
                "email": data.user.email,
                "phone_number": data.user.phone_number,
                "address": {
                    "first_name": data.user.address.first_name,
                    "last_name": data.user.address.last_name,
                    "street": data.user.address.street,
                    "city": data.user.address.city,
                    "postal_code": data.user.address.postal_code,
                    "country": data.user.address.country,
                }
            });
        }
    }

    public static eventCategoryView(data: CategoryViewJson): void {
        const ids = [];

        data.products.forEach((v) => {
            ids.push(v.id);
        });

        this.ecomm({
            ecomm_prodid: ids,
            ecomm_pagetype: 'category',
        });

        const dl = {
            currencyCode: data.currency,
            impressions: [],
        };

        data.products.forEach((v, ix) => {
            dl.impressions.push({
                list: data.listname,
                position: ix + 1,

                id: v.id,
                cartid: v.cartid,
                price: v.price,
                currency: v.currency,
                category: v.category,
            });
        });

        this.pushDlEvent('categoryView', data);
    }

    public static eventProductView(product: JsProductInfo): void {
        this.ecomm({
            ecomm_prodid: product.id,
            ecomm_pagetype: 'product',
            ecomm_totalvalue: product.price,
        });

        this.pushDlEvent('productView', {
            detail: {
                products: [{
                    name: product.name,
                    id: product.id,
                    cartid: product.cartid,
                    price: product.price,
                    currency: product.currency,
                    category: product.category,
                }],
            },
        });

        this.fbTrack('ViewContent', {
            content_ids: [product.id],
            content_name: product.name,
            content_type: 'product',
            value: product.price,
            currency: product.currency,
        });
    }

    public static eventAddToCart(data: AddCartProductInfo): void {
        this.pushDlEvent('addToCart', {
            currencyCode: data.currency,
            add: {
                products: [data],
            },
        });

        this.fbTrack('AddToCart', {
            content_name: data.name,
            content_ids: [data.id],
            content_type: 'product',
            value: data.price,
            currency: data.currency,
        });
    }

    public static eventRemoveFromCart(data: AddCartProductInfo): void {
        this.pushDlEvent('removeFromCart', {
            currencyCode: data.currency,
            add: {
                products: [data],
            },
        });
    }

    public static eventChangeCart(product: JsProductInfo, prev: number, current: number): void {
        const dq = Math.abs(current - prev);

        const data: AddCartProductInfo = <any>product;
        data.quantity = dq;

        if (dq >= 0) {
            this.eventAddToCart(data);
        } else {
            this.eventRemoveFromCart(data);
        }
    }

    public static eventCart(cart: CartJson): void {
        const ids = [];

        cart.products.forEach((v) => {
            ids.push(v.id);
        });

        this.pushDlEvent('checkout', {
            checkout: {
                actionField: {
                    step: 1,
                    option: 'cart',
                },

                products: cart.products,
            }
        });
    }

    public static eventCartUser(cart: CartJson, type: string): void {
        const dl = [];

        cart.products.forEach((info) => {
            dl.push({
                name: info.name,
                id: info.id,
                price: info.price,
                category: info.category,
                quantity: info.quantity,
            });
        });

        this.pushDlEvent('checkout', {
            checkout: {
                actionField: {
                    step: 2,
                    option: 'order',
                },

                products: dl,
            }
        });

        switch (type) {
            case 'login':
                this.pushDlEvent('checkout', {
                    checkout: {
                        actionField: {
                            step: 2,
                            option: 'BRAK LOGOWANIA',
                        },

                        products: dl,
                    }
                });
                break;

            case 'verify':
                this.pushDlEvent('checkout', {
                    checkout: {
                        actionField: {
                            step: 2,
                            option: 'UZUPEŁNIANIE DANYCH',
                        },

                        products: dl,
                    }
                });
                break;
        }
    }

    public static eventCartCheckout(cart: CartJson): void {
        const dl = [];

        cart.products.forEach((v) => {
            dl.push({
                name: v.name,
                id: v.id,
                price: v.price,
                category: v.category,
                quantity: v.quantity,
            });
        });

        this.pushDlEvent('checkout', {
            checkout: {
                actionField: {
                    step: 3,
                    option: 'delivery',
                },

                products: dl,
            }
        });
    }

    public static eventPurchase(data: PurchaseInfo): void {
        const product_ids = [];

        const fbdata = {
            value: data.totalprice,
            currency: data.currency,
            content_type: 'product',

            contents: [],
        };

        data.products.forEach((v) => {
            product_ids.push(v.id);

            fbdata.contents.push({
                id: v.id,
                item_price: v.price,
                quantity: v.quantity,
            });
        });

        if (data.user) {
            this.pushDlEvent('user_data', {
                "email": data.user.email,
                "phone_number": data.user.phone_number,
                "address": {
                    "first_name": data.user.address.first_name,
                    "last_name": data.user.address.last_name,
                    "street": data.user.address.street,
                    "city": data.user.address.city,
                    "postal_code": data.user.address.postal_code,
                    "country": data.user.address.country,
                }
            });
        }

        this.fbTrack('Purchase', fbdata);

        this.ecomm({
            ecomm_prodid: [product_ids],
            ecomm_pagetype: 'purchase',
            ecomm_totalvalue: data.totalprice,
        });

        this.gtag('event', 'purchase', {
            allow_custom_scripts: true,
            value: data.totalprice,
            transaction_id: data.order_id,
            send_to: 'DC-9891313/sales/konwe0+transactions'
        });

        this.gtag('event', 'conversion', {
            send_to: 'AW-742207556/o6CuCJbMhNIDEMTg9OEC',
            value: data.totalprice,
            currency: data.currency,
            transaction_id: data.order_id,
        });

        const dl = {
            purchase: {
                actionField: {
                    id: data.order_id,
                    affiliation: 'venus-beauty.pl',
                    revenue: data.totalprice,
                    shipping: data.shippingprice,
                    tax: data.tax,
                },

                products: [],
            }
        };

        data.products.forEach((v) => {
            dl.purchase.products.push({
                name: v.title,
                id: v.id,
                price: v.price,
                sku: v.sku,
                quantity: v.quantity,
                category: v.category?.title,
            });
        });


        this.pushDlEvent('purchase', dl);
    }

    public static event(event: string, data?: any): void {
        console.log('event', event, data);

        switch (event) {
            case 'addtocart':
                this.eventAddToCart(data);
                break;

            case 'product':
                this.eventProductView(data);
                break;

            case 'register-view':
                // {{ utils.google_conversion('J5utCI3WvQkQ67SYywM') }}
                break;

            case 'register':
            case 'register-b2b':
                this.fbTrack('CompleteRegistration');
                break;

            case 'contact-view':
                // {{ utils.google_conversion('BOv8CJXVvQkQ67SYywM') }}
                break;

            case 'content':
                this.fbTrack('Contact');
                break;

            case 'catalogue-view':
                // {{ utils.google_conversion('WoAwCLXRvQkQ67SYywM') }}
                break;

            case 'update-services':
                this.fbTrack('AddPaymentInfo');
                break;

            case 'update-delivery':
                this.pushDlEvent('checkoutOption', {
                    checkout_option: {
                        actionField: {
                            step: 3,
                            option: data.servicetitle,
                        },
                    },
                });
                break;

            default:
                throw 'Unknown action ' + event;
        }
    }

    public static async accept() {
        this.accepted = true;

        for (const action of this.actions) {
            await action();
        }
    }
}
